import React from 'react';
import useAmplitude from 'utils/hooks/useAmplitude';
import { AMPLITUDE_EVENT_TYPES, AMPLITUDE_WALLET_PROPERTY_VALUES } from 'constants/amplitude';
import { toAnalyticsCurrency } from 'utils/walletUtil';
import { useEditWalletLowBalanceAlert } from 'services/reactQueryHooks/wallet';
import AvailableBalance from './AvailableBalance';
import SpentThisMonth from './SpentThisMonth';
import LastTopUp from './LastTopUp';
import LowBalanceAlert from './LowBalanceAlert';
import AddFunds from './AddFunds';
import WalletDetails from './WalletDetails';
import BalanceHistory from './BalanceHistory';
import Footer from './Footer';
import { isWalletInactive } from './helper';

import styles from './style.module.scss';

const WalletDashboard = ({
  dashboard,
  isHistoryFetching = false,
  pageNumber = 1,
  transactionType = [],
  transactionStatus = [],
  orderBy = null,
  onPageChange,
  onPageSizeChange,
  onTransactionTypeChange,
  onTransactionStatusChange,
  onOrderByChange,
  onStatusChange,
}) => {
  const { history } = dashboard;
  // Frozen and closed wallets are read-only: greyed stats, no alert editing, no top-ups.
  const inactive = isWalletInactive(dashboard.status);
  const { sendAmplitudeEvent } = useAmplitude();
  const { mutate: editLowBalanceAlert } = useEditWalletLowBalanceAlert({
    currency: dashboard.currency,
  });

  const handleSaveLowBalanceAlert = (valueMajor, inputMethod) => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.walletLowBalanceAlertSaveButtonClicked, {
      currency: toAnalyticsCurrency(dashboard.currency),
      surface: AMPLITUDE_WALLET_PROPERTY_VALUES.surface.walletEdit,
      amount: valueMajor,
      inputMethod,
    });
    editLowBalanceAlert({
      walletId: dashboard.walletId,
      lowBalanceAlertThresholdMinor: Math.round(valueMajor * 100),
    });
  };

  return (
    <div className={styles.dashboard} data-testid="walletDashboard">
      <div className={styles.statRow}>
        <AvailableBalance
          balance={dashboard.balance}
          alertThreshold={dashboard.alertThreshold}
          status={dashboard.status}
          statusChangedAt={dashboard.statusChangedAt}
        />
        <SpentThisMonth amount={dashboard.spentThisMonth} inactive={inactive} />
        <LastTopUp
          amount={dashboard.lastTopUp?.amount}
          date={dashboard.lastTopUp?.date}
          paymentMethod={dashboard.lastTopUp?.paymentMethod}
          inactive={inactive}
        />
      </div>
      <div className={styles.actionRow}>
        <LowBalanceAlert
          threshold={dashboard.alertThresholdMajor}
          onSave={handleSaveLowBalanceAlert}
          canEdit={!inactive}
          currency={dashboard.currency}
        />
        {inactive ? (
          <WalletDetails walletName={dashboard.walletName} currency={dashboard.currency} />
        ) : (
          <AddFunds walletId={dashboard.walletId} currency={dashboard.currency} />
        )}
      </div>
      <BalanceHistory
        items={history.items}
        currency={dashboard.currency}
        pageNumber={pageNumber}
        totalCount={history.totalCount}
        isFetching={isHistoryFetching}
        transactionType={transactionType}
        transactionStatus={transactionStatus}
        orderBy={orderBy}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onTransactionTypeChange={onTransactionTypeChange}
        onTransactionStatusChange={onTransactionStatusChange}
        onOrderByChange={onOrderByChange}
      />
      <Footer
        status={dashboard.status}
        remainingBalance={dashboard.balance}
        balanceMajor={dashboard.balanceMajor}
        currency={dashboard.currency}
        onStatusChange={onStatusChange}
      />
    </div>
  );
};

export default WalletDashboard;
